/**
 * FeatureToggle Component
 *
 * One feature flag as a labelled switch: a visually hidden checkbox with
 * `role="switch"`, so it keeps native keyboard and form behaviour.
 */

import { useCallback, useId, type ChangeEvent } from 'react';
import './FeatureToggle.css';

export interface FeatureToggleProps {
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  /** The shown value differs from what is stored. */
  readonly isChanged: boolean;
  /** The feature is still in beta. */
  readonly isBeta: boolean;
  readonly disabled: boolean;
  readonly onChange: (enabled: boolean) => void;
}

export function FeatureToggle({
  label,
  description,
  checked,
  isChanged,
  isBeta,
  disabled,
  onChange,
}: FeatureToggleProps): JSX.Element {
  const inputId = useId();
  const descriptionId = useId();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked),
    [onChange]
  );

  return (
    <div className={`feature-toggle${disabled ? ' feature-toggle--disabled' : ''}`}>
      <div className="feature-toggle__text">
        <label className="feature-toggle__label" htmlFor={inputId}>
          {label}
          {isBeta && <span className="feature-toggle__beta">Beta</span>}
          {isChanged && <span className="feature-toggle__unsaved">Unsaved</span>}
        </label>
        <p className="feature-toggle__description" id={descriptionId}>
          {description}
        </p>
      </div>
      <label className="feature-toggle__switch-wrapper" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          role="switch"
          className="feature-toggle__input"
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          aria-describedby={descriptionId}
        />
        <span className="feature-toggle__switch" aria-hidden="true">
          <span className="feature-toggle__thumb" />
        </span>
      </label>
    </div>
  );
}
